import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Descriptions,
  Tag,
  Progress,
  Button,
  Space,
  Tabs,
  Table,
  Row,
  Col,
  Timeline,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Checkbox,
  message,
  Typography,
  Alert,
} from 'antd';
import {
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  LoginOutlined,
  ArrowLeftOutlined,
  ShopOutlined,
  UserOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  SwapOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTenantDetail, useTenantStats } from '../../hooks';
import { TenantActivityLog } from './TenantActivityLog';
import { MetricCard } from '../../components';
import { tenantService, TenantStaffMember, TenantStore } from '../../services';
import { PLANS, TENANT_STATUS } from '@erpgold/shared';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const planColors: Record<string, string> = {
  [PLANS.Lite]: 'default',
  [PLANS.Starter]: 'blue',
  [PLANS.Professional]: 'gold',
  [PLANS.Enterprise]: 'purple',
};

const planLabels: Record<string, string> = {
  [PLANS.Lite]: 'Lite',
  [PLANS.Starter]: 'Starter',
  [PLANS.Professional]: 'Professional',
  [PLANS.Enterprise]: 'Enterprise',
};

// BO can receive 'pending' in addition to shared TENANT_STATUS values.
const statusColors: Record<string, string> = {
  [TENANT_STATUS.ACTIVE]: 'success',
  trial: 'cyan',
  [TENANT_STATUS.GRACE]: 'orange',
  [TENANT_STATUS.SUSPENDED]: 'error',
  [TENANT_STATUS.DELETED]: 'default',
  pending: 'default',
};

const statusLabels: Record<string, string> = {
  [TENANT_STATUS.ACTIVE]: 'Hoạt động',
  trial: 'Dùng thử',
  [TENANT_STATUS.GRACE]: 'Gia hạn',
  [TENANT_STATUS.SUSPENDED]: 'Tạm ngưng',
  [TENANT_STATUS.DELETED]: 'Đã xóa',
  pending: 'Chờ xử lý',
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  const maybe = error as { response?: { data?: { message?: string } }; message?: string };
  return maybe?.response?.data?.message || maybe?.message || fallback;
};

export const TenantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isSuspendModalVisible, setIsSuspendModalVisible] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [form] = Form.useForm();
  const [isMisaSyncing, setIsMisaSyncing] = useState(false);

  const [isExtendModalVisible, setIsExtendModalVisible] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [sendExtendEmail, setSendExtendEmail] = useState(true);

  const [isPlanModalVisible, setIsPlanModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [sendPlanEmail, setSendPlanEmail] = useState(true);

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [sendDeleteEmail, setSendDeleteEmail] = useState(true);

  const { tenant, loading: tenantLoading, updateTenant, refetch } = useTenantDetail(id || '');
  const { stats, loading: statsLoading } = useTenantStats(id || '');

  // Staff & Stores tabs real data
  const [staff, setStaff] = useState<TenantStaffMember[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffTotal, setStaffTotal] = useState(0);
  const [staffPage, setStaffPage] = useState(1);

  const [stores, setStores] = useState<TenantStore[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storesTotal, setStoresTotal] = useState(0);
  const [storesPage, setStoresPage] = useState(1);

  const fetchStaff = useCallback(async (page = 1) => {
    if (!id) return;
    setStaffLoading(true);
    try {
      const res = await tenantService.getTenantStaff(id, { page, limit: 20 });
      setStaff(res.items);
      setStaffTotal(res.meta.total);
      setStaffPage(page);
    } catch {
      // silently fail – table will stay empty
    } finally {
      setStaffLoading(false);
    }
  }, [id]);

  const fetchStores = useCallback(async (page = 1) => {
    if (!id) return;
    setStoresLoading(true);
    try {
      const res = await tenantService.getTenantStores(id, { page, limit: 20 });
      setStores(res.items);
      setStoresTotal(res.meta.total);
      setStoresPage(page);
    } catch {
      // silently fail – table will stay empty
    } finally {
      setStoresLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);
  useEffect(() => { fetchStores(); }, [fetchStores]);

  const handleUpdateTenant = async (values: any) => {
    try {
      await updateTenant(values);
      message.success('Tenant updated successfully');
      setIsEditModalVisible(false);
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to update tenant'));
    }
  };

  const handleSuspend = async () => {
    try {
      if (!id) return;
      if (!suspendReason.trim()) {
        message.warning('Please enter reason for suspension');
        return;
      }
      await tenantService.suspendTenant(id, suspendReason || 'Admin suspension');
      message.success('Tenant suspended successfully');
      setIsSuspendModalVisible(false);
      setSuspendReason('');
      await refetch();
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to suspend tenant'));
    }
  };

  const handleActivate = async () => {
    try {
      if (!id) return;
      await tenantService.activateTenant(id);
      message.success('Tenant activated successfully');
      await refetch();
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to activate tenant'));
    }
  };

  const handleImpersonate = async () => {
    try {
      if (!id) return;
      const payload = await tenantService.impersonateTenant(id);
      const hasQuery = payload.redirect_url.includes('?');
      const redirectUrl = `${payload.redirect_url}${hasQuery ? '&' : '?'}impersonation_token=${encodeURIComponent(payload.impersonation_token)}`;
      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
      message.success('Impersonation session opened in a new tab');
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to impersonate tenant'));
    }
  };

  const handleMisaSync = async () => {
    try {
      if (!id) return;
      setIsMisaSyncing(true);
      await tenantService.triggerMisaSync(id);
      message.success('MISA sync triggered — jobs enqueued for this tenant');
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to trigger MISA sync'));
    } finally {
      setIsMisaSyncing(false);
    }
  };

  const handleExtendTrial = () => {
    setExtendDays(7);
    setSendExtendEmail(true);
    setIsExtendModalVisible(true);
  };

  const handleExtendTrialConfirm = async () => {
    if (!id) return;
    try {
      await tenantService.extendTrial(id, extendDays);
      message.success('Trial extended successfully');
      setIsExtendModalVisible(false);
      await refetch();
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to extend trial'));
    }
  };

  const handleChangePlan = () => {
    setSelectedPlan(tenant?.plan || '');
    setSendPlanEmail(true);
    setIsPlanModalVisible(true);
  };

  const handleChangePlanConfirm = async () => {
    if (!id || !selectedPlan) return;
    try {
      await tenantService.changePlan(id, selectedPlan);
      message.success('Plan changed successfully');
      setIsPlanModalVisible(false);
      await refetch();
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to change plan'));
    }
  };

  const handleDelete = () => {
    setDeleteConfirmName('');
    setSendDeleteEmail(true);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id || deleteConfirmName !== tenant?.name) return;
    try {
      await tenantService.deleteTenant(id);
      message.success('Tenant deleted successfully');
      setIsDeleteModalVisible(false);
      navigate('/tenants');
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to delete tenant'));
    }
  };

  if (tenantLoading || !tenant) {
    return (
      <div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/tenants')}
          style={{ marginBottom: 16 }}
        >
          Back to Tenants
        </Button>
        <Card loading />
      </div>
    );
  }

  // BE response field snake_case (ADR-013)
  const activityData = [
    {
      action: 'Tenant Created',
      timestamp: tenant.created_at,
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
    },
    {
      action: 'Plan upgraded to Professional',
      timestamp: tenant.updated_at,
      icon: <DollarOutlined style={{ color: '#1890ff' }} />,
    },
    {
      action: 'First store created',
      timestamp: tenant.created_at,
      icon: <ShopOutlined style={{ color: '#722ed1' }} />,
    },
  ];

  return (
    <div>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/tenants')}
        style={{ marginBottom: 16 }}
      >
        Back to Tenants
      </Button>

      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space direction="vertical" size={4}>
            <Title level={3} style={{ margin: 0 }}>{tenant.name}</Title>
            <Text type="secondary">{tenant.email}</Text>
          </Space>
        </Col>
        <Col>
          <Space>
            <Tag color={planColors[tenant.plan]}>{planLabels[tenant.plan]}</Tag>
            <Tag color={statusColors[tenant.status]}>{statusLabels[tenant.status] || tenant.status}</Tag>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                form.setFieldsValue(tenant);
                setIsEditModalVisible(true);
              }}
            >
              Edit
            </Button>
            <Button
              icon={<LoginOutlined />}
              onClick={handleImpersonate}
            >
              Impersonate
            </Button>
            <Button
              icon={<SyncOutlined spin={isMisaSyncing} />}
              onClick={handleMisaSync}
              loading={isMisaSyncing}
            >
              Sync MISA
            </Button>
            {tenant.status === 'trial' && (
              <Button
                icon={<ClockCircleOutlined />}
                onClick={handleExtendTrial}
              >
                Extend Trial
              </Button>
            )}
            <Button
              icon={<SwapOutlined />}
              onClick={handleChangePlan}
            >
              Change Plan
            </Button>
            {tenant.status === TENANT_STATUS.ACTIVE || tenant.status === 'trial' ? (
              <Button
                danger
                icon={<PauseCircleOutlined />}
                onClick={() => setIsSuspendModalVisible(true)}
              >
                Suspend
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleActivate}
              >
                Activate
              </Button>
            )}
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <MetricCard
            title="Revenue"
            value={stats?.revenue || 0}
            prefix="₫"
            icon={<DollarOutlined />}
            color="#52c41a"
            loading={statsLoading}
          />
        </Col>
        <Col span={6}>
          <MetricCard
            title="Total Orders"
            value={stats?.orders || 0}
            icon={<ShoppingCartOutlined />}
            color="#1890ff"
            loading={statsLoading}
          />
        </Col>
        <Col span={6}>
          <MetricCard
            title="Users"
            value={tenant.user_count}
            icon={<UserOutlined />}
            color="#722ed1"
          />
        </Col>
        <Col span={6}>
          <MetricCard
            title="Stores"
            value={tenant.store_count}
            icon={<ShopOutlined />}
            color="#fa8c16"
          />
        </Col>
      </Row>

      <Tabs defaultActiveKey="overview">
        <TabPane tab="Overview" key="overview">
          <Row gutter={16}>
            <Col span={16}>
              <Card title="Tenant Information">
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Company Name">{tenant.name}</Descriptions.Item>
                  <Descriptions.Item label="Email">{tenant.email}</Descriptions.Item>
                  <Descriptions.Item label="Phone">{tenant.phone || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Plan">
                    <Tag color={planColors[tenant.plan]}>{planLabels[tenant.plan]}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={statusColors[tenant.status]}>{statusLabels[tenant.status] || tenant.status}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Created">
                    {dayjs(tenant.created_at).format('YYYY-MM-DD HH:mm')}
                  </Descriptions.Item>
                  {tenant.expires_at && (
                    <Descriptions.Item label="Expires">
                      {dayjs(tenant.expires_at).format('YYYY-MM-DD')}
                    </Descriptions.Item>
                  )}
                </Descriptions>
                <div style={{ marginTop: 16 }}>
                  <Title level={5} style={{ marginBottom: 12 }}>Usage</Title>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text>Users</Text>
                      <Progress percent={Math.min(100, Math.round(((stats?.users || tenant.user_count || 0) / 50) * 100))} />
                    </div>
                    <div>
                      <Text>Stores</Text>
                      <Progress percent={Math.min(100, Math.round(((stats?.stores || tenant.store_count || 0) / 10) * 100))} status="active" />
                    </div>
                    <div>
                      <Text>Products</Text>
                      <Progress percent={Math.min(100, Math.round(((stats?.products || 0) / 1000) * 100))} status="normal" />
                    </div>
                  </Space>
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card title="Recent Activity">
                <Timeline>
                  {activityData.map((item, index) => (
                    <Timeline.Item key={index} dot={item.icon}>
                      <Text>{item.action}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {dayjs(item.timestamp).format('YYYY-MM-DD HH:mm')}
                      </Text>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Statistics" key="statistics">
          <Card title="Tenant Statistics">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Total Orders">{stats?.orders || 0}</Descriptions.Item>
              <Descriptions.Item label="Total Revenue">₫{(stats?.revenue || 0).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Total Users">{stats?.users || tenant.user_count || 0}</Descriptions.Item>
              <Descriptions.Item label="Total Stores">{stats?.stores || tenant.store_count || 0}</Descriptions.Item>
              <Descriptions.Item label="Total Products">{stats?.products || 0}</Descriptions.Item>
              <Descriptions.Item label="Storage Used">{stats?.storage_used || 0} MB</Descriptions.Item>
              <Descriptions.Item label="Last Login">
                {stats?.last_login_at ? dayjs(stats.last_login_at).format('YYYY-MM-DD HH:mm') : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Last Order">
                {stats?.last_order_at ? dayjs(stats.last_order_at).format('YYYY-MM-DD HH:mm') : 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </TabPane>

        <TabPane tab="Stores" key="stores">
          <Card title="Stores">
            <Table
              rowKey="_id"
              loading={storesLoading}
              columns={[
                { title: 'Name', dataIndex: 'name', key: 'name' },
                { title: 'Code', dataIndex: 'code', key: 'code' },
                { title: 'Address', dataIndex: 'full_address', key: 'full_address', ellipsis: true },
                {
                  title: 'Status',
                  dataIndex: 'state',
                  key: 'state',
                  render: (v: number) => <Tag color={v === 1 ? 'success' : 'default'}>{v === 1 ? 'Active' : 'Inactive'}</Tag>,
                },
                {
                  title: 'Created',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
                },
              ]}
              dataSource={stores}
              pagination={{
                current: storesPage,
                total: storesTotal,
                pageSize: 20,
                onChange: (p) => fetchStores(p),
                showTotal: (t) => `Total ${t} stores`,
              }}
              locale={{ emptyText: 'No stores available' }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Employees" key="users">
          <Card title="Tenant Employees">
            <Table
              rowKey="_id"
              loading={staffLoading}
              columns={[
                { title: 'Name', dataIndex: 'name', key: 'name' },
                { title: 'Email', dataIndex: 'email', key: 'email' },
                { title: 'Username', dataIndex: 'username', key: 'username' },
                { title: 'Role', dataIndex: 'role', key: 'role' },
                {
                  title: 'Status',
                  dataIndex: 'state',
                  key: 'state',
                  render: (v: number) => <Tag color={v === 1 ? 'success' : 'default'}>{v === 1 ? 'Active' : 'Inactive'}</Tag>,
                },
                {
                  title: 'Created',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
                },
              ]}
              dataSource={staff}
              pagination={{
                current: staffPage,
                total: staffTotal,
                pageSize: 20,
                onChange: (p) => fetchStaff(p),
                showTotal: (t) => `Total ${t} employees`,
              }}
              locale={{ emptyText: 'No users available' }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Activity Logs" key="activity-log">
          <TenantActivityLog tenantId={id || ''} />
        </TabPane>
      </Tabs>

      {/* Edit Tenant Modal */}
      <Modal
        title="Edit Tenant"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateTenant}
        >
          <Form.Item
            name="name"
            label="Company Name"
            rules={[{ required: true, message: 'Please enter company name' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter valid email' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Phone Number">
            <Input />
          </Form.Item>
          <Form.Item name="plan" label="Plan">
            <Select>
              <Option value={PLANS.Lite}>Lite</Option>
              <Option value={PLANS.Starter}>Starter</Option>
              <Option value={PLANS.Professional}>Professional</Option>
              <Option value={PLANS.Enterprise}>Enterprise</Option>
            </Select>
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select>
              <Option value={TENANT_STATUS.ACTIVE}>Active</Option>
              <Option value="trial">Trial</Option>
              <Option value={TENANT_STATUS.SUSPENDED}>Suspended</Option>
              <Option value="pending">Pending</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Suspend Modal */}
      <Modal
        title="Suspend Tenant"
        open={isSuspendModalVisible}
        onCancel={() => {
          setIsSuspendModalVisible(false);
          setSuspendReason('');
        }}
        onOk={handleSuspend}
        okButtonProps={{ danger: true, disabled: !suspendReason.trim() }}
      >
        <p>Are you sure you want to suspend <strong>{tenant.name}</strong>?</p>
        <Input.TextArea
          placeholder="Enter reason for suspension"
          value={suspendReason}
          onChange={(e) => setSuspendReason(e.target.value)}
          rows={4}
        />
      </Modal>

      {/* Extend Trial Modal */}
      <Modal
        title="Extend Trial"
        open={isExtendModalVisible}
        onCancel={() => {
          setIsExtendModalVisible(false);
          setExtendDays(7);
        }}
        onOk={handleExtendTrialConfirm}
        width={400}
      >
        <p>Extend trial period for <strong>{tenant.name}</strong></p>
        <Form.Item label="Number of days">
          <InputNumber
            min={1}
            max={90}
            value={extendDays}
            onChange={(value) => setExtendDays(value || 7)}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Checkbox
          checked={sendExtendEmail}
          onChange={(e) => setSendExtendEmail(e.target.checked)}
        >
          Send notification email to tenant
        </Checkbox>
      </Modal>

      {/* Change Plan Modal */}
      <Modal
        title="Change Plan"
        open={isPlanModalVisible}
        onCancel={() => {
          setIsPlanModalVisible(false);
          setSelectedPlan('');
        }}
        onOk={handleChangePlanConfirm}
        okButtonProps={{ disabled: !selectedPlan || selectedPlan === tenant?.plan }}
        width={400}
      >
        <p>
          Current plan: <Tag color={planColors[tenant.plan]}>{planLabels[tenant.plan]}</Tag>
        </p>
        <Form.Item label="New Plan">
          <Select
            value={selectedPlan}
            onChange={setSelectedPlan}
            style={{ width: '100%' }}
          >
            <Option value={PLANS.Lite}>Lite</Option>
            <Option value={PLANS.Starter}>Starter</Option>
            <Option value={PLANS.Professional}>Professional</Option>
            <Option value={PLANS.Enterprise}>Enterprise</Option>
          </Select>
        </Form.Item>
        <Checkbox
          checked={sendPlanEmail}
          onChange={(e) => setSendPlanEmail(e.target.checked)}
        >
          Send notification email to tenant
        </Checkbox>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Tenant"
        open={isDeleteModalVisible}
        okType="danger"
        okButtonProps={{ disabled: deleteConfirmName !== tenant?.name }}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setDeleteConfirmName('');
        }}
        onOk={handleDeleteConfirm}
        width={500}
      >
        <Alert
          type="error"
          message="This action cannot be undone"
          description="The tenant will be marked for deletion and permanently removed after 90 days."
          showIcon
          style={{ marginBottom: 16 }}
        />
        <p>
          Type <strong>{tenant.name}</strong> to confirm deletion:
        </p>
        <Input
          value={deleteConfirmName}
          onChange={(e) => setDeleteConfirmName(e.target.value)}
          placeholder={tenant.name}
          style={{ marginBottom: 16 }}
        />
        <Checkbox
          checked={sendDeleteEmail}
          onChange={(e) => setSendDeleteEmail(e.target.checked)}
        >
          Send notification email to tenant
        </Checkbox>
      </Modal>
    </div>
  );
};

export default TenantDetail;
